import React, { FC, memo, useCallback, useMemo } from 'react'
import DropDown from '@sport1/news-styleguide/DropDown'
import NonFlexingContainer from '@sport1/news-styleguide/NonFlexingContainer'
import CardHeadline from '@sport1/news-styleguide/CardHeadline'
import dayjs from 'dayjs'
import 'dayjs/locale/de'
import isToday from 'dayjs/plugin/isToday'
import { fetchEPGData } from '../utils/fetchEPGData'
import BaseTVProgram from '../base'
import { getDates, getHours } from '../utils/timing'
import {
    DateSelectType,
    FetchingType,
    TimeSelectType,
    TVProgramData,
    TVProgramProps,
} from '../types'
import Image from '@/components/StaticImage'

dayjs.extend(isToday)

const DEFAULT_DATE_ID = '1'
const DEFAULT_TIME_ID = '0'

const DEFAULT_LOOKBACK_HOURS = 24
const DEFAULT_LOOKAHEAD_HOURS = 24

const TVProgramExtended: FC<TVProgramProps> = ({ url }) => {
    const [epgData, setEpgData] = React.useState<TVProgramData>()
    const [selectedDate, setSelectedDate] = React.useState<Date | undefined>()
    const [fetchingData, setFetchingData] = React.useState<FetchingType>({
        dateId: DEFAULT_DATE_ID,
        timeId: DEFAULT_TIME_ID,
        date: dayjs().toISOString(),
        lookbackHours: DEFAULT_LOOKBACK_HOURS,
        lookaheadHours: DEFAULT_LOOKAHEAD_HOURS,
    })
    const [isLoading, setIsLoading] = React.useState(false)

    const dates = useMemo(() => getDates(), [])
    const hours = useMemo(() => getHours(dayjs(fetchingData.date).isToday()), [fetchingData])

    React.useEffect(() => {
        setIsLoading(true)
        let requestUrl = url
        requestUrl = requestUrl
            .replace('{timestamp}', `${fetchingData.date}`)
            .replace('{lookbackHours}', `${fetchingData.lookbackHours}`)
            .replace('{lookaheadHours}', `${fetchingData.lookaheadHours}`)
        fetchEPGData(requestUrl).then(epg => {
            setIsLoading(false)
            setSelectedDate(dayjs(fetchingData.date).toDate())
            setEpgData(epg.epgData)
        })
    }, [fetchingData, url])

    const onDateSelect = useCallback((option: DateSelectType) => {
        const { value } = option
        if (value) {
            setFetchingData({
                dateId: value.id,
                timeId: DEFAULT_TIME_ID,
                date: value.date,
                lookbackHours: DEFAULT_LOOKBACK_HOURS,
                lookaheadHours: DEFAULT_LOOKAHEAD_HOURS,
            })
        }
    }, [])

    const onTimeSelect = useCallback(
        (option: TimeSelectType) => {
            const { value } = option
            if (value) {
                setFetchingData({
                    dateId: fetchingData.dateId,
                    timeId: value.id,
                    date: dayjs(fetchingData.date)
                        .set('hour', value.hour !== undefined ? value.hour : dayjs().get('hour'))
                        .set(
                            'minute',
                            value.minute !== undefined ? value.minute : dayjs().get('minute')
                        )
                        .toISOString(),
                    lookbackHours: value.lookbackHours,
                    lookaheadHours: DEFAULT_LOOKAHEAD_HOURS,
                })
            }
        },
        [fetchingData]
    )

    return epgData ? (
        <NonFlexingContainer backgroundColor="pearl">
            <NonFlexingContainer
                marginLeft={['spacing-7', 'spacing-7', 'spacing-7', 'spacing-9']}
                marginRight={['spacing-7', 'spacing-7', 'spacing-7', 'spacing-9']}
                spaceTop="standard"
                backgroundColor="pearl"
            >
                <CardHeadline
                    cardHeadlineType={['cardHeadlineS', 'cardHeadlineM', 'cardHeadlineM']}
                    text="Programm"
                />
            </NonFlexingContainer>
            <NonFlexingContainer
                horizontal
                marginLeft={['spacing-7', 'spacing-7', 'spacing-7', 'spacing-9']}
                spaceTop="standard"
                centerVertical
                testID="program-dropdown-container"
            >
                <DropDown
                    width={200}
                    options={dates}
                    onOptionChange={onDateSelect}
                    selectedOption={dates.find(date => date.value.id === fetchingData.dateId)}
                />
                <div className="ml-3 hidden tn:block">
                    <DropDown
                        width={200}
                        options={hours}
                        onOptionChange={onTimeSelect}
                        selectedOption={hours.find(hour => hour.value.id === fetchingData.timeId)}
                    />
                </div>
                <NonFlexingContainer>
                    {isLoading ? (
                        <Image
                            src="/images/spinner/loading_spinner.svg"
                            alt="Loading data"
                            width="40"
                            height="40"
                        />
                    ) : null}
                </NonFlexingContainer>
            </NonFlexingContainer>
            <BaseTVProgram epgData={epgData} selectedDate={selectedDate} />
        </NonFlexingContainer>
    ) : null
}

export default memo(TVProgramExtended)
